/**
 * 后期处理层(方案 F):暗角 / 噪点 / 闪白 / 震屏 / 全局调色。
 * 暗角+噪点常驻;闪白与震屏由效果驱动;调色随 SkyState 缓变。
 */
import { Sprite, TilingSprite, Texture, ColorMatrixFilter, Container } from 'pixi.js';
import { P } from '../engine/Palette';
import { sky } from '../engine/SkyRenderer';

/** 暗角:白心→灰边,multiply(方案 F1) */
export class Vignette {
  sprite: Sprite;
  constructor() {
    const size = 512, c = document.createElement('canvas');
    c.width = c.height = size;
    const ctx = c.getContext('2d')!;
    const g = ctx.createRadialGradient(size / 2, size / 2, size * 0.18, size / 2, size / 2, size * 0.72);
    g.addColorStop(0, '#ffffff');
    g.addColorStop(1, 'rgb(150,150,150)'); // ≈ 边缘压暗 40%
    ctx.fillStyle = g; ctx.fillRect(0, 0, size, size);
    this.sprite = new Sprite(Texture.from(c));
    this.sprite.blendMode = 'multiply';
  }
  resize(w: number, h: number) { this.sprite.width = w; this.sprite.height = h; }
}

/** 噪点:128px 白噪 TilingSprite,alpha 0.02,每帧抖位(方案 F2) */
export class Grain {
  sprite: TilingSprite;
  constructor() {
    const size = 128, c = document.createElement('canvas');
    c.width = c.height = size;
    const ctx = c.getContext('2d')!;
    const img = ctx.createImageData(size, size);
    for (let i = 0; i < img.data.length; i += 4) {
      const v = (Math.random() * 255) | 0;
      img.data[i] = img.data[i + 1] = img.data[i + 2] = v; img.data[i + 3] = 255;
    }
    ctx.putImageData(img, 0, 0);
    this.sprite = new TilingSprite({ texture: Texture.from(c), width: 10, height: 10 });
    this.sprite.alpha = 0.02;
  }
  resize(w: number, h: number) { this.sprite.width = w; this.sprite.height = h; }
  update() { this.sprite.tilePosition.set(Math.random() * 128, Math.random() * 128); }
}

/** 闪白:暖白(divine),alpha 由效果驱动(方案 F4) */
export class Flash {
  sprite: Sprite;
  private a = 0; private decay = 0;
  constructor() {
    this.sprite = new Sprite(Texture.WHITE);
    this.sprite.tint = P.divine; this.sprite.alpha = 0;
  }
  resize(w: number, h: number) { this.sprite.width = w; this.sprite.height = h; }
  /** 立刻升到 peak,再在 downSec 内衰减 */
  flash(peak = 1, downSec = 0.35) { this.a = Math.max(this.a, peak); this.decay = peak / Math.max(0.01, downSec); }
  hold(a: number) { this.a = a; this.decay = 0; }
  update(dt: number) {
    if (this.decay > 0 && this.a > 0) this.a = Math.max(0, this.a - this.decay * dt);
    this.sprite.alpha = this.a;
  }
}

/** 震屏:移动世界容器 position,上限 4px,0.4s 衰减,禁旋转(方案 F5) */
export class Shake {
  ox = 0; oy = 0;
  private amt = 0; private t = 0; private dur = 0.4;
  shake(amount: number, durSec = 0.4) {
    this.amt = Math.min(4, Math.max(this.amt, amount)); this.dur = durSec; this.t = durSec;
  }
  update(dt: number) {
    if (this.t > 0) {
      this.t -= dt;
      const k = Math.max(0, this.t / this.dur);
      const a = this.amt * k;
      this.ox = (Math.random() * 2 - 1) * a;
      this.oy = (Math.random() * 2 - 1) * a;
      if (this.t <= 0) { this.ox = this.oy = 0; this.amt = 0; }
    }
  }
}

/** 全局调色:ColorMatrixFilter 随 SkyState(方案 F3) + 混沌纪压制。
 *  chaos ∈ [0,1]:混沌纪的世界应"灰暗、等待被点亮"(EPOCHS[0].event 原文),
 *  1 = 饱和度压到 0.4、亮度 0.78——黎明纪跃迁时从 1 缓落到 0,即"世界上色"。 */
export class ColorGrade {
  filter = new ColorMatrixFilter();
  target: Container | null = null;
  chaos = 0;
  update() {
    const amb = sky.state.ambient;
    const k = this.chaos;
    this.filter.reset();
    // 混沌 = 褪色而非昏暗(实调结论):压暗+去饱和会把暖色天搅成脏褐泥;
    // 低饱和(0.22)+低对比(0.16)+微降亮(0.9) = 灰蓝寂静的"待上色老照片",
    // 黎明跃迁的金色从这里起跳,落差最大。night()/colorTone() 两滤镜实测不可用
    // (前者压成纯黑,后者整屏辐射绿),勿再尝试。
    this.filter.saturate((0.9 + 0.1 * amb) * (1 - k) + 0.22 * k, false);
    if (k > 0.001) {
      this.filter.contrast(0.16 * k, true);
      this.filter.brightness(1 - 0.1 * k, true);
    }
  }
}
